import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { PlusIcon, SearchIcon, DownloadIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Devices() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newDevice, setNewDevice] = useState({
    serialNumber: '',
    model: '',
    os: 'WINDOWS',
    notes: ''
  });
  const navigate = useNavigate();

  const fetchDevices = useCallback(async (search = '') => {
    try {
      const token = localStorage.getItem('admin_token');
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await axios.get(`${API}/admin/devices${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDevices(response.data);
    } catch (error) {
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Debounced search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchDevices(searchTerm);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, fetchDevices]);

  const handleCreateDevice = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(`${API}/admin/devices`, newDevice, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Device created successfully');
      setIsDialogOpen(false);
      setNewDevice({ serialNumber: '', model: '', os: 'WINDOWS', notes: '' });
      fetchDevices(searchTerm);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create device');
    }
  };

  const handleExportDevices = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/export/devices`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `devices_export_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Devices exported successfully');
    } catch (error) {
      toast.error('Failed to export devices');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: 'bg-green-100 text-green-800',
      LOCKED: 'bg-orange-100 text-orange-800',
      STOLEN: 'bg-red-100 text-red-800',
      OVERDUE: 'bg-yellow-100 text-yellow-800',
      SECURITY_ALERT: 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div data-testid="devices-page">
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">Devices</h1>
          <p className="text-gray-600 mt-2">Manage all rental laptops</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportDevices}>
            <DownloadIcon className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-device-button">
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Device
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add New Device</DialogTitle>
                <DialogDescription>Enter device details to add it to the system</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateDevice} className="space-y-4">
              <div>
                <Label htmlFor="serialNumber">Serial Number</Label>
                <Input
                  id="serialNumber"
                  value={newDevice.serialNumber}
                  onChange={(e) => setNewDevice({...newDevice, serialNumber: e.target.value})}
                  required
                  data-testid="device-serial-input"
                />
              </div>
              <div>
                <Label htmlFor="model">Model</Label>
                <Input
                  id="model"
                  value={newDevice.model}
                  onChange={(e) => setNewDevice({...newDevice, model: e.target.value})}
                  required
                  data-testid="device-model-input"
                />
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Input
                  id="notes"
                  value={newDevice.notes}
                  onChange={(e) => setNewDevice({...newDevice, notes: e.target.value})}
                  data-testid="device-notes-input"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="create-device-button">Create Device</Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="mb-6">
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            placeholder="Search by serial, model, notes, license key, IP, city..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-devices-input"
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Search matches: serial number, BIOS serial, model, manufacturer, notes, Windows/Office license keys, IP, city
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {devices.map((device) => (
          <Card key={device.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(`/devices/${device.id}`)} data-testid={`device-card-${device.id}`}>
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle className="text-lg">{device.model}</CardTitle>
                <Badge className={getStatusColor(device.status)} data-testid={`device-status-${device.id}`}>
                  {device.status}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-gray-500">Serial:</span>
                  <span className="ml-2 font-medium font-mono text-xs">{device.serialNumber}</span>
                </div>
                {device.biosSerial && device.biosSerial !== device.serialNumber && (
                  <div>
                    <span className="text-gray-500">BIOS:</span>
                    <span className="ml-2 font-medium font-mono text-xs">{device.biosSerial}</span>
                  </div>
                )}
                <div>
                  <span className="text-gray-500">OS:</span>
                  <span className="ml-2 font-medium">{device.os}</span>
                </div>
                {device.lastSeenAt && (
                  <div>
                    <span className="text-gray-500">Last Seen:</span>
                    <span className="ml-2 font-medium">{new Date(device.lastSeenAt).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {devices.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No devices found</p>
        </div>
      )}
    </div>
  );
}